import { LANGUAGES, REASON_FIELDS, THEME_FIELDS, variantText } from "./catalogView"
import { CREATED_ITEM, itemIdOf } from "./catalogOperations"

// The add and edit form of the Setup tab (docs/design/tournament-feedback, „Formularul”) as data: what each translated row
// allows, and the commands a Save sends (sportya-api docs/tournament-feedback.md, one change per command).

export const FORM_FIELDS = { theme: THEME_FIELDS, reason: REASON_FIELDS }
export const LEGENDS = { title: "Title", reasonsLabel: "Reasons label", text: "Reason" }

const pick = (fields, read) => Object.fromEntries(fields.map(([field]) => [field, read(field)]))
const trimmed = (fields, texts) => pick(fields, field => (texts?.[field] ?? "").trim())
const within = (fields, texts, limits) => fields.every(([field, , limitKey]) => texts[field] && texts[field].length <= limits[limitKey])

// The form of an item, or of a new one: its Romanian texts, its icon, and per language how its translation is being handled
// (`saved`, `manual` once „Correct by hand” is clicked, `automatic` once „Use the automatic translation” is) with the texts.
export function initialForm(kind, item) {
  const fields = FORM_FIELDS[kind]
  return {
    ro: pick(fields, field => item?.ro?.[field] ?? (field === "reasonsLabel" ? "Ce n-a mers?" : "")),
    icon: item?.icon || "",
    variants: Object.fromEntries(LANGUAGES.map(([language]) => [language,
      { mode: "saved", content: pick(fields, field => item?.variants?.[language]?.content?.[field] ?? "") }])),
  }
}

// How a translated row of a saved item behaves: `pending` (translating, read-only), `required` (none to show: written by hand),
// `manual` (corrected by hand, editable), `default` (the catalog's approved default text, read-only until „Correct by hand”) or
// `automatic` (the automatic translation, read-only until „Correct by hand”).
export function variantState(variant, mode) {
  if (mode === "manual") return "manual"
  if (mode === "automatic") return "automatic"
  if (variant?.status === "pending") return "pending"
  if (variant?.origin === "manual") return "manual"
  if (!variant?.content || ["missing", "manualRequired"].includes(variant?.status)) return "required"
  if (variant?.origin === "default") return "default"
  return "automatic"
}

// The text players see now for a field, which they keep until a changed Romanian text is translated again.
export const playersSee = (variant, field, ro) => (variant?.status === "translated" && variantText(variant, field))
  || variantText({ content: variant?.previous }, field) || variantText(variant, field) || ro

// Whether a saved item's Romanian texts differ from the form's.
export const romanianChanged = (kind, item, form) => !!item && FORM_FIELDS[kind].some(([field]) => (form.ro[field] ?? "").trim() !== item.ro?.[field])

// The English and Bulgarian written by hand with a new or changed Romanian text, on a server that does not translate: one
// correction per language, for the item the Save creates or edits.
const handWritten = (fields, form, limits, itemId, revision) => {
  const corrections = LANGUAGES.map(([language]) => ({ action: "correctTranslation", itemId, revision, language,
    content: trimmed(fields, form.variants[language].content) }))
  return { corrections, valid: corrections.every(({ content }) => within(fields, content, limits)) }
}

// `{ commands, valid }` of a Save: one command per change, in order — the Romanian text and the icon in one `edit`, then each
// language's correction or return to the automatic translation (not when the Romanian text changes: it is translated again).
// `manualTranslation`: this server does not translate (`translation.enabled: false` in the catalog read), so a new or changed
// Romanian text is saved with its English and Bulgarian, written in the form.
export function formCommands({ kind, theme, item }, form, limits, { manualTranslation = false } = {}) {
  const fields = FORM_FIELDS[kind]
  const ro = trimmed(fields, form.ro)
  let valid = within(fields, ro, limits) && (kind !== "theme" || !!form.icon)
  if (!item) {
    const create = kind === "theme" ? { action: "createTheme", ro, icon: form.icon } : { action: "createReason", themeCode: theme.code, ro }
    if (!manualTranslation) return { valid, commands: [create] }
    const written = handWritten(fields, form, limits, CREATED_ITEM, 1)
    return { valid: valid && written.valid, commands: [create, ...written.corrections] }
  }
  const itemId = itemIdOf(item), revision = item.revision
  const roChanged = romanianChanged(kind, item, form)
  const iconChanged = kind === "theme" && form.icon !== item.icon
  const commands = []
  if (roChanged || iconChanged) commands.push({ action: "edit", itemId, revision, ...(roChanged && { ro }), ...(iconChanged && { icon: form.icon }) })
  if (roChanged && manualTranslation) {
    const written = handWritten(fields, form, limits, itemId, revision)
    valid = valid && written.valid
    commands.push(...written.corrections)
  }
  if (!roChanged) {
    for (const [language] of LANGUAGES) {
      const variant = item.variants?.[language], { mode, content } = form.variants[language]
      const state = variantState(variant, mode)
      if (mode === "automatic" && variant?.origin === "manual") commands.push({ action: "useAutomatic", itemId, revision, language })
      else if (state === "manual" || state === "required") {
        const texts = trimmed(fields, content)
        if (fields.some(([field]) => texts[field] !== (variant?.content?.[field] ?? ""))) {
          valid = valid && within(fields, texts, limits)
          commands.push({ action: "correctTranslation", itemId, revision, language, content: texts })
        }
      }
    }
  }
  return { valid: valid && commands.length > 0, commands }
}
